import { AuthenticationService, HistoryService, HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { APPLICATION_ENDPOINTS } from '../constants/application-endpoints';
import { APPLICATION_ROUTES } from '../constants/application-routes';
import { TermsOfUseType } from '../constants/terms-of-use-type';

interface TermsOfUse {
  nrSeqTermo?: number;
}

interface TargetPair {
  parameters?: Record<string, unknown>,
  path: string,
}

interface RequiredConsent {
  check: RequiredConsentCheck
  content: RequiredConsentContent
}

interface RequiredConsentCheck {
  endpoint: TargetPair,
}

interface RequiredConsentContent {
  endpoint: TargetPair,
  route: TargetPair,
}

interface RequiredConsentCheckResult {
  consented: boolean,
  route?: TargetPair,
  sequence?: number,
}

const requiredConsents: Array<RequiredConsent> = [
  {
    check: {
      endpoint: {
        parameters: { termType: TermsOfUseType.TERM_OF_USE, },
        path: APPLICATION_ENDPOINTS.CHECK_TERMS_OF_USE,
      }
    },
    content: {
      endpoint: {
        parameters: { termType: TermsOfUseType.TERM_OF_USE, },
        path: APPLICATION_ENDPOINTS.GET_TERMS_OF_USE_SEQUENCE,
      },
      route: {
        parameters: { termType: TermsOfUseType.TERM_OF_USE, },
        path: APPLICATION_ROUTES.ASSIGN_TERMS_OF_USE,
      }
    },
  },
  {
    check: {
      endpoint: { path: APPLICATION_ENDPOINTS.CHECK_PRIVACY },
    },
    content: {
      endpoint: { path: APPLICATION_ENDPOINTS.GET_PRIVACY_SEQUENCE },
      route: { path: APPLICATION_ROUTES.ASSIGN_PRIVACY },
    },
  },
];

export default class ApplicationTermService {

  private static allConsented = false;
  private static pendingCheck: Promise<void> | null = null;

  static async hasSignedRequiredConsents(): Promise<void> {
    if (this.allConsented) {
      return;
    }

    if (this.pendingCheck) {
      await this.pendingCheck;
      return;
    }

    const requestConsent = async (content: RequiredConsentContent): Promise<RequiredConsentCheckResult> => {
      const response = await HttpService.get<TermsOfUse>(content.endpoint.path, content.endpoint.parameters);
      return { consented: false, route: content.route, sequence: response.body?.nrSeqTermo };
    };

    const checkConsent = async (endpoint: TargetPair): Promise<boolean> => {
      const response = await HttpService.get<TermsOfUse>(endpoint.path, endpoint.parameters);
      return response.body?.nrSeqTermo != null;
    };

    const toCheckConsent = async (requiredConsent: RequiredConsent): Promise<RequiredConsentCheckResult> => {
      const consented = await checkConsent(requiredConsent.check.endpoint);
      return consented ? { consented } : requestConsent(requiredConsent.content);
    };

    const checkRequiredConsents = async (): Promise<void> => {
      const results = await Promise.all(requiredConsents.map(toCheckConsent));

      if (results.every(result => result.consented)) {
        return;
      }

      const pendingConsent = results.find(result => !result.consented);
      if (pendingConsent && pendingConsent.route && pendingConsent.sequence) {
        HistoryService.push(pendingConsent.route.path, { nrSeqTermo: pendingConsent.sequence, ...pendingConsent.route.parameters });
        throw new Error('Pending required consent');
      }

      /**
       * TO-DO Refactor me.
       * Redirect to a error screen showing what is missing instead of should logout the user.
       */
      AuthenticationService.logout();
      throw new Error('Erro while checking required consents');
    };

    this.pendingCheck = checkRequiredConsents().then(() => {
      this.allConsented = true;
    });

    try {
      await this.pendingCheck;
    } finally {
      this.pendingCheck = null;
    }
  }
}
